import React from "react";
import PropTypes from "prop-types";
import { fmtEurSigned } from "./format";

// LEVER_HINTS provides a one-line recommendation based on the dominant driver.
const LEVER_HINTS = {
  volume: "Optimize efficiency or commit a reservation to control usage growth.",
  price: "Renegotiate contracts or move to reserved / committed-use capacity.",
  other: "Investigate composition shift — mix of services may be changing.",
};

const ROWS = [
  {
    key: "volume",
    label: "Volume",
    // blue series for volume
    barColor: "hsl(var(--chart-1))",
    bgClass: "bg-primary-soft",
    textClass: "text-primary",
  },
  {
    key: "price",
    label: "Price",
    // warning for price
    barColor: "hsl(var(--warning))",
    bgClass: "bg-warning-soft",
    textClass: "text-warning",
  },
  {
    key: "other",
    label: "Other",
    // neutral for other
    barColor: "hsl(var(--chart-pred))",
    bgClass: "bg-surface-2",
    textClass: "text-text-2",
  },
];

export default function DriverWaterfall({ decomposition }) {
  const d = decomposition || {};
  const volume = d.volume ?? 0;
  const price = d.price ?? 0;
  const other = d.other ?? 0;

  const values = { volume, price, other };
  const absValues = { volume: Math.abs(volume), price: Math.abs(price), other: Math.abs(other) };
  const maxAbs = Math.max(...Object.values(absValues), 1);

  // Determine dominant component (largest absolute value). When every component
  // is zero there's no real driver, so default to 'other' instead of letting the
  // sort tie fall back to "volume" and show a misleading lever-specific hint.
  const maxComponentAbs = Math.max(...Object.values(absValues));
  const dominantKey =
    maxComponentAbs === 0
      ? "other"
      : Object.entries(absValues).sort((a, b) => b[1] - a[1])[0][0];
  const hint = LEVER_HINTS[dominantKey] || LEVER_HINTS.other;

  return (
    <div className="rounded-card border border-card-bd bg-card px-5 py-4">
      <div className="mb-3 text-xs font-medium uppercase tracking-wide text-text-2">
        Why it&rsquo;ll move
      </div>

      <div className="space-y-2.5">
        {ROWS.map(({ key, label, barColor, bgClass, textClass }) => {
          const val = values[key];
          const widthPct = (Math.abs(val) / maxAbs) * 100;
          const isDominant = key === dominantKey;

          return (
            <div key={key} className="flex items-center gap-3">
              {/* Label */}
              <div className="w-14 shrink-0 text-right">
                <span
                  className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${bgClass} ${textClass}`}
                >
                  {label}
                </span>
              </div>

              {/* Bar track */}
              <div className="flex-1 rounded-full bg-surface-2" style={{ height: 10 }}>
                <div
                  className="rounded-full transition-all"
                  style={{
                    height: 10,
                    width: `${widthPct.toFixed(1)}%`,
                    backgroundColor: barColor,
                    opacity: isDominant ? 1 : 0.55,
                  }}
                />
              </div>

              {/* Value */}
              <div
                className={`w-24 shrink-0 text-right tabular-nums text-xs font-medium ${
                  val > 0
                    ? "text-danger"
                    : val < 0
                    ? "text-success"
                    : "text-text-2"
                }`}
              >
                {fmtEurSigned(val)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lever hint */}
      <div className="mt-3.5 rounded-card bg-surface-2 px-3 py-2 text-xs text-text-2">
        <span className="font-medium text-text">Lever: </span>
        {hint}
      </div>
    </div>
  );
}

DriverWaterfall.propTypes = {
  decomposition: PropTypes.shape({
    volume: PropTypes.number,
    price: PropTypes.number,
    other: PropTypes.number,
  }),
};
